import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@navi-soft/ui/button";
import { inputOnAppClassName, TextField } from "@navi-soft/ui/text-field";
import {
  ErrorAlertProvider,
  useErrorAlert,
} from "@/components/error-alert-dialog";
import { cn } from "@/lib/cn";
import { login } from "@/lib/api";

const LANDING_ORIGIN = import.meta.env.VITE_LANDING_ORIGIN ?? "https://navi-soft.ru";
const SOFT_LOGO = "/brand/soft_simple.png";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  return (
    <ErrorAlertProvider>
      <LoginForm />
    </ErrorAlertProvider>
  );
}

function LoginForm() {
  const t = useTranslations("auth");
  const navigate = useNavigate();
  const { showError } = useErrorAlert();
  const [loginName, setLoginName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const ok = await login(loginName, password);
      if (!ok) {
        showError(t("errorInvalid"));
        return;
      }
      await navigate({ to: "/" });
    } catch {
      showError(t("errorUnavailable"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-app p-6">
      <div className="w-full max-w-sm space-y-4 rounded-lg border border-border-subtle bg-surface p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <a
            href={LANDING_ORIGIN}
            className="shrink-0 outline-none"
            aria-label="Navi Soft"
          >
            <img
              src={SOFT_LOGO}
              alt=""
              width={36}
              height={36}
              className="size-9 object-contain"
            />
          </a>
          <h1 className="m-0 text-ui-title font-semibold text-primary">
            {t("loginTitle")}
          </h1>
        </div>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
          <TextField
            size="lg"
            type="text"
            name="login"
            label={t("loginLabel")}
            autoComplete="username"
            autoFocus
            value={loginName}
            onChange={(e) => setLoginName(e.target.value)}
            className={cn("w-full", inputOnAppClassName)}
            required
          />
          <TextField
            size="lg"
            type="password"
            name="password"
            label={t("passwordLabel")}
            showPasswordLabel={t("showPassword")}
            hidePasswordLabel={t("hidePassword")}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={cn("w-full", inputOnAppClassName)}
            required
          />
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={submitting}
          >
            {submitting ? t("submitting") : t("submit")}
          </Button>
        </form>
      </div>
    </div>
  );
}
